/**
 * Sectioned Box Widget
 *
 * bundle_price checked: static pricing from metaobject "price" field
 * (already includes discount). Prices never change on quantity.
 *
 * bundle_price unchecked: dynamic pricing from selected products.
 *
 * use_total_amount_override checked: users pick freely across all boxes
 * up to maxAmount total. Per-box sizeRange is ignored.
 *
 * use_total_amount_override unchecked: per-box min/max from sizeRange.
 */

class SectionedBoxWidget {
  constructor() {
    if (!window.sectionedBoxData || !window.skioDynamicBoxes || !window.bundleProducts) return;

    this.data = window.sectionedBoxData;
    this.boxes = window.skioDynamicBoxes;
    this.products = window.bundleProducts;
    this.gifts = this.data.gifts || window.sectionedBoxGifts || [];
    this.currency = window.shopCurrency || { symbol: 'kr', code: 'SEK' };

    this.isBundlePrice = !!this.data.bundlePrice;
    this.useTotalOverride = !!this.data.useTotalOverride;
    this.useTotalMinOverride = !!this.data.useTotalMinOverride;
    this.maxQuantity = this.data.maxQuantity || 0;
    this.minQuantity = this.data.minQuantity || 0;
    this.showSubscription = this.data.showSubscription;

    this.selectedOption = this.showSubscription ? 'subscription' : 'onetime';
    this.selectedSellingPlan = null;

    this.qtyByBox = {};
    this.boxes.forEach((box, i) => { if (box) this.qtyByBox[i] = {}; });

    this.init();
  }

  fmt(cents) {
    const v = cents / 100;
    const r = Math.round(v * 10) / 10;
    return `${(r % 1) !== 0 ? r.toFixed(1) : Math.round(r)} ${this.currency.symbol}`;
  }

  slugifyReadable(value) {
    return String(value || '')
      .replace(/[åÅ]/g, 'a')
      .replace(/[äÄ]/g, 'a')
      .replace(/[öÖ]/g, 'o')
      .replace(/[ëË]/g, 'e')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '') || 'na';
  }

  applyCustomBoxReadableIds() {
    const counts = {};
    const nextId = (base) => {
      counts[base] = (counts[base] || 0) + 1;
      return counts[base] > 1 ? `${base}-${counts[base]}` : base;
    };
    const contentToken = (el, fallback) => {
      const raw = (el?.getAttribute('aria-label') || el?.textContent || '').trim();
      const token = this.slugifyReadable(raw);
      return token !== 'na' ? token : fallback;
    };

    document.querySelectorAll('.product-item').forEach((item) => {
      const productName = this.slugifyReadable(
        item.dataset.productTitle ||
        item.querySelector('.product-name')?.textContent ||
        item.dataset.productId ||
        ''
      );
      if (productName === 'na') return;
      item.querySelectorAll('.quantity-btn.increase').forEach((btn) => {
        btn.id = nextId(`product-custom-box-sectioned-btn-add-${productName}`);
      });
      item.querySelectorAll('.quantity-btn.decrease').forEach((btn) => {
        btn.id = nextId(`product-custom-box-sectioned-btn-decrease-${productName}`);
      });
      item.querySelectorAll('.quantity-input').forEach((input) => {
        input.id = nextId(`product-custom-box-sectioned-input-quantity-${productName}`);
      });
    });

    document.querySelectorAll('.js-add-to-cart, #addToCart').forEach((btn) => {
      btn.id = nextId('product-custom-box-sectioned-btn-add-to-cart');
    });

    // Keep frequency controls untouched (editor + app hooks rely on stable #frequency selectors),
    // but still tag subscription/onetime toggles for click tracking.
    document.querySelectorAll('.subscription-box').forEach((el) => {
      el.id = nextId('product-custom-box-sectioned-div-subscription');
    });
    document.querySelectorAll('.onetime-box').forEach((el) => {
      el.id = nextId('product-custom-box-sectioned-div-onetime');
    });
  }

  parseSellingPlanId(val) {
    if (val == null) return NaN;
    const s = String(val).trim();
    if (s.startsWith('gid://shopify/SellingPlan/')) {
      return parseInt(s.replace('gid://shopify/SellingPlan/', ''), 10);
    }
    return parseInt(s, 10);
  }

  init() {
    this.populateFrequency();
    this.setupEvents();
    this.initQty();

    if (this.showSubscription) this.selectOption('subscription');
    else this.selectOption('onetime');

    this.updateDescriptions();
    this.updateIncreaseBtns();
    this.setupObserver();
    this.updateInfoIcons();
    this.applyCustomBoxReadableIds();
  }

  // ── Frequency ────────────────────────────────────────────────────

  populateFrequency() {
    const sel = document.getElementById('frequency');
    if (!sel) return;
    const box = this.boxes.find(b => b);
    const plans = box?.box_config?.sellingPlanGroup;
    if (!plans?.length) { sel.innerHTML = '<option value="">No plans</option>'; return; }

    sel.innerHTML = '';
    const sorted = [...plans].sort((a, b) => a.position - b.position);
    const wk = window.boxTranslations?.frequency || 'veckor';
    const mo = window.boxTranslations?.monthFrequency || 'månader';

    sorted.forEach(p => {
      const o = document.createElement('option');
      o.value = p.gid;
      const int = p.interval.toLowerCase();
      o.textContent = int === 'week' ? `${p.intervalCount} ${wk}` : int === 'month' ? `${p.intervalCount} ${mo}` : p.title || `${p.intervalCount} ${int}`;
      sel.appendChild(o);
    });

    if (sorted.length) { sel.value = sorted[0].gid; this.selectedSellingPlan = sorted[0]; }
  }

  // ── Events ───────────────────────────────────────────────────────

  setupEvents() {
    if (this.showSubscription) {
      document.querySelector('.subscription-box')?.addEventListener('click', () => this.selectOption('subscription'));
      document.querySelector('.onetime-box')?.addEventListener('click', () => this.selectOption('onetime'));

      const sel = document.getElementById('frequency');
      if (sel) {
        const plans = this.boxes.find(b => b)?.box_config?.sellingPlanGroup || [];
        sel.addEventListener('change', e => {
          this.selectedSellingPlan = plans.find(p => p.gid === e.target.value);
          this.updateTotals();
          this.updateCartBtn();
        });
      }
    }

    this.setupQtyControls();
    document.querySelectorAll('.js-add-to-cart, #addToCart').forEach(b => b.addEventListener('click', () => this.addToCart()));
    window.addEventListener('resize', () => this.updateInfoIcons());
  }

  // ── Quantities ───────────────────────────────────────────────────

  initQty() {
    document.querySelectorAll('.product-item').forEach(item => {
      const pid = item.dataset.productId;
      const bi = parseInt(item.dataset.boxIndex) || 0;
      const inp = item.querySelector('.quantity-input');
      if (inp) { if (!this.qtyByBox[bi]) this.qtyByBox[bi] = {}; this.qtyByBox[bi][pid] = parseInt(inp.value) || 0; }
    });
  }

  boxQty(i) { return Object.values(this.qtyByBox[i] || {}).reduce((s, q) => s + q, 0); }

  totalQty() {
    let t = 0;
    Object.values(this.qtyByBox).forEach(bq => { t += Object.values(bq).reduce((s, q) => s + q, 0); });
    return t;
  }

  boxRange(i) {
    if (this.useTotalOverride) return { min: 0, max: this.maxQuantity || Infinity };
    const sr = this.boxes[i]?.box_config?.sizeRange;
    if (sr?.length >= 2) return { min: sr[0], max: sr[1] };
    return { min: 1, max: 99 };
  }

  canIncrease(boxIdx) {
    if (this.useTotalOverride) return this.maxQuantity === 0 || this.totalQty() < this.maxQuantity;
    if (this.useTotalMinOverride && this.maxQuantity > 0 && this.totalQty() >= this.maxQuantity) return false;
    return this.boxQty(boxIdx) < this.boxRange(boxIdx).max;
  }

  allFilled() {
    if (this.useTotalOverride) {
      const total = this.totalQty();
      if (this.maxQuantity > 0 && total < this.maxQuantity) return false;
      if (this.maxQuantity === 0 && total === 0) return false;
      return true;
    }
    if (this.useTotalMinOverride && this.minQuantity > 0) {
      if (this.totalQty() < this.minQuantity) return false;
    }
    for (let i = 0; i < this.boxes.length; i++) {
      if (!this.boxes[i]) continue;
      if (this.useTotalMinOverride) continue;
      if (this.boxQty(i) < this.boxRange(i).min) return false;
    }
    return true;
  }

  updateIncreaseBtns() {
    document.querySelectorAll('.product-container').forEach(c => {
      const bi = parseInt(c.dataset.boxIndex) || 0;
      const blocked = !this.canIncrease(bi);
      c.querySelectorAll('.quantity-btn.increase').forEach(b => {
        b.style.opacity = blocked ? '0.3' : '1';
        b.style.pointerEvents = blocked ? 'none' : '';
      });
    });
  }

  productUnit(n) {
    const t = window.boxTranslations || {};
    return n === 1 ? (t.productSingular || 'produkt') : (t.productPlural || 'produkter');
  }

  updateDescriptions() {
    const t = window.boxTranslations || {};
    const addExact = t.addExact || 'Lägg till';
    const addAtLeast = t.addAtLeast || 'Lägg till minst';
    const addMax = t.addMax || 'Lägg till max';

    if (this.useTotalOverride) {
      document.querySelectorAll('.box-group-requirement').forEach(el => {
        if (this.maxQuantity === 0) { el.textContent = ''; el.style.display = 'none'; return; }
        const remaining = this.maxQuantity - this.totalQty();
        if (remaining > 0) { el.textContent = `${addExact} ${remaining} ${this.productUnit(remaining)}`; el.style.display = ''; }
        else { el.textContent = ''; el.style.display = 'none'; }
      });
      this.updateCartRequirement('');
      return;
    }

    if (this.useTotalMinOverride && this.minQuantity > 0) {
      const total = this.totalQty();
      const remaining = this.minQuantity - total;
      // Hide per-box messages — this is a bundle-wide requirement
      document.querySelectorAll('.box-group-requirement').forEach(el => {
        el.textContent = ''; el.style.display = 'none';
      });
      this.updateCartRequirement(remaining > 0 ? `${addAtLeast} ${remaining} ${this.productUnit(remaining)}` : '');
      return;
    }

    document.querySelectorAll('.box-group-requirement').forEach(el => {
      const bi = parseInt(el.dataset.boxIndex) || 0;
      const { min, max } = this.boxRange(bi);
      const cur = this.boxQty(bi);
      let txt = '';

      if (min === max && min > 0) {
        const r = min - cur;
        if (r > 0) txt = `${addExact} ${r} ${this.productUnit(r)}`;
      } else if (min > 0) {
        const r = min - cur;
        if (r > 0) txt = `${addAtLeast} ${r} ${this.productUnit(r)}`;
      } else if (max < 99) {
        const r = max - cur;
        if (r > 0) txt = `${addMax} ${r} ${this.productUnit(r)}`;
      }

      if (txt) { el.textContent = txt; el.style.display = ''; }
      else { el.textContent = ''; el.style.display = 'none'; }
    });
    this.updateCartRequirement('');
  }

  updateCartRequirement(text) {
    let el = document.querySelector('.js-cart-requirement-msg');
    if (!text) { if (el) el.style.display = 'none'; return; }
    if (!el) {
      el = document.createElement('p');
      el.className = 'js-cart-requirement-msg';
      el.style.cssText = 'font-size:14px;font-weight:500;text-align:center;margin:8px 0 0;color:#000;';
      const cart = document.querySelector('.desktop-cart-container') || document.querySelector('.cart-container');
      if (cart) cart.appendChild(el);
      else return;
    }
    el.textContent = text;
    el.style.display = '';
  }

  setupQtyControls() {
    document.querySelectorAll('.product-item').forEach(item => {
      const pid = item.dataset.productId;
      const bi = parseInt(item.dataset.boxIndex) || 0;
      const inc = item.querySelector('.quantity-btn.increase');
      const dec = item.querySelector('.quantity-btn.decrease');
      const inp = item.querySelector('.quantity-input');
      if (!inc || !dec || !inp) return;

      // Respect metaobject stock flags: if in_stock is false, hide quantity controls
      // and show a red "In stock [date]" tag instead (product cannot be selected).
      const productData = this.products.find(p => {
        const baseId = p.product.id.replace('gid://shopify/Product/', '');
        // Sectioned data may carry a _boxIndex to disambiguate products reused across boxes
        return baseId === pid && (p._boxIndex === undefined || p._boxIndex === bi);
      }) || this.products.find(p => p.product.id.replace('gid://shopify/Product/', '') === pid);

      const rawStock = productData?.in_stock;
      const isInStock = !(rawStock === false || rawStock === 'false' || rawStock === 0 || rawStock === '0');
      if (!isInStock) {
        const backInStock = productData?.back_in_stock_text;
        const outText = productData?.out_of_stock_text || 'In stock';
        const container = item.querySelector('.quantity-selector') || inp.parentElement;
        if (container) {
          container.innerHTML = '';
          const tag = document.createElement('span');
          tag.textContent = backInStock ? `${outText} ${backInStock}` : outText;
          tag.style.display = 'inline-flex';
          tag.style.alignItems = 'center';
          tag.style.justifyContent = 'center';
          tag.style.padding = '4px 10px';
          tag.style.borderRadius = '999px';
          tag.style.backgroundColor = '#b84040';
          tag.style.color = '#ffffff';
          tag.style.fontSize = '12px';
          tag.style.fontWeight = '600';
          container.appendChild(tag);
        }
        // Ensure internal quantity state is zero and no listeners are attached
        if (!this.qtyByBox[bi]) this.qtyByBox[bi] = {};
        this.qtyByBox[bi][pid] = 0;
        return;
      }

      if (parseInt(inp.value) === 0) { inp.classList.add('hidden'); dec.classList.add('hidden'); }

      inc.addEventListener('click', () => {
        if (!this.canIncrease(bi)) return;
        const cur = parseInt(inp.value) || 0;
        inp.value = cur + 1;
        if (!this.qtyByBox[bi]) this.qtyByBox[bi] = {};
        this.qtyByBox[bi][pid] = cur + 1;
        if (cur === 0) { inp.classList.remove('hidden'); dec.classList.remove('hidden'); }
        dec.disabled = false;
        this.onChange();
      });

      dec.addEventListener('click', () => {
        const cur = parseInt(inp.value) || 0;
        if (cur <= 0) return;
        inp.value = cur - 1;
        if (!this.qtyByBox[bi]) this.qtyByBox[bi] = {};
        this.qtyByBox[bi][pid] = cur - 1;
        if (cur - 1 === 0) { inp.classList.add('hidden'); dec.classList.add('hidden'); }
        dec.disabled = cur - 1 === 0;
        this.onChange();
      });
    });
  }

  onChange() {
    if (!this.isBundlePrice) { this.updateProductPrices(); this.updateTotals(); }
    this.updateCartBtn();
    this.updateDescriptions();
    this.updateIncreaseBtns();
    this.updateInfoIcons();
    this.applyCustomBoxReadableIds();
  }

  // ── Option selection ─────────────────────────────────────────────

  selectOption(opt) {
    this.selectedOption = opt;
    const subBox = document.querySelector('.subscription-box');
    const oneBox = document.querySelector('.onetime-box');
    const freqC = document.querySelector('.subscription-frequency-container-wrapper');
    const bar = document.querySelector('.best-value-bar');
    const prices = document.querySelectorAll('.product-price');

    if (opt === 'subscription') {
      subBox?.classList.add('selected'); oneBox?.classList.remove('selected');
      freqC?.style.setProperty('display', 'flex'); bar?.classList.remove('hidden');
      document.querySelectorAll('.product-container').forEach(c => c.classList.add('subscription-selected'));
      prices.forEach(el => { el.style.color = '#387A62'; el.style.fontWeight = '600'; });
    } else {
      oneBox?.classList.add('selected'); subBox?.classList.remove('selected');
      freqC?.style.setProperty('display', 'none'); bar?.classList.add('hidden');
      document.querySelectorAll('.product-container').forEach(c => c.classList.remove('subscription-selected'));
      prices.forEach(el => { el.style.color = '#6b7280'; el.style.fontWeight = '500'; });
    }

    this.updateProductPrices();
    this.updateTotals();
    this.updateCartBtn();
  }

  // ── Pricing ──────────────────────────────────────────────────────

  sellingPlanPct() {
    const sp = this.selectedSellingPlan || this.boxes.find(b => b)?.box_config?.sellingPlanGroup?.[0];
    if ((sp?.priceAdjustmentType || 'PERCENTAGE') === 'PERCENTAGE') return (sp?.priceAdjustmentAmount || 20) / 100;
    return 0;
  }

  dynamicTotal() {
    let t = 0;
    this.products.forEach(p => {
      const pid = p.product.id.replace('gid://shopify/Product/', '');
      let q = 0;
      Object.values(this.qtyByBox).forEach(bq => { if (bq[pid]) q += bq[pid]; });
      if (q > 0) t += p.product.price_min * q;
    });
    return t;
  }

  prices() {
    const spd = this.sellingPlanPct();

    if (this.isBundlePrice) {
      const onetime = this.data.onetimePrice;
      const hasOverride = this.data.subscriptionPriceOverride !== null && this.data.subscriptionPriceOverride !== undefined && this.data.subscriptionPriceOverride !== '';
      const override = hasOverride ? Number(this.data.subscriptionPriceOverride) : NaN;
      const sub = Number.isFinite(override) ? Math.round(override) : Math.round(onetime * (1 - spd));
      return { sub, onetime, original: onetime };
    }

    const dyn = this.dynamicTotal();
    return { sub: Math.round(dyn * (1 - spd)), onetime: dyn, original: dyn };
  }

  updateProductPrices() {
    if (this.isBundlePrice) return;
    const mult = this.selectedOption === 'subscription' ? (1 - this.sellingPlanPct()) : 1.0;
    document.querySelectorAll('.product-item').forEach(item => {
      const pid = item.dataset.productId;
      const el = item.querySelector('.product-price');
      if (!el) return;
      const pd = this.products.find(p => p.product.id.replace('gid://shopify/Product/', '') === pid);
      if (pd) el.textContent = this.fmt(pd.product.price_min * mult);
    });
  }

  updateTotals() {
    const { sub, onetime } = this.prices();
    const bags = this.totalQty();

    const subEl = document.querySelector('.subscription-price');
    if (subEl) subEl.textContent = this.fmt(sub);

    const subStrike = document.querySelector('.subscription-onetime-total-strikethrough');
    if (subStrike) { subStrike.textContent = this.fmt(onetime); subStrike.style.display = onetime > 0 ? '' : 'none'; }

    const subMeals = document.querySelector('.subscription-box .total-meals');
    if (subMeals) subMeals.textContent = bags > 0 ? `${bags} produkt${bags > 1 ? 'er' : ''}` : '';

    const oneEl = document.querySelector('.onetime-price');
    if (oneEl) oneEl.textContent = this.fmt(onetime);

    const oneStrike = document.querySelector('.onetime-perceived-value-strikethrough');
    if (oneStrike) oneStrike.style.display = 'none';

    const oneMeals = document.querySelector('.onetime-box .total-meals');
    if (oneMeals) oneMeals.textContent = bags > 0 ? `${bags} produkt${bags > 1 ? 'er' : ''}` : '';

    const savings = onetime - sub;
    const savEl = document.querySelector('.best-value-bar .savings-amount');
    if (savEl) savEl.textContent = this.fmt(savings);
  }

  // ── Cart button ──────────────────────────────────────────────────

  updateCartBtn() {
    const btns = document.querySelectorAll('.js-add-to-cart, #addToCart');
    const ok = this.allFilled();
    const { sub, onetime } = this.prices();
    const price = this.selectedOption === 'subscription' ? sub : onetime;
    const txt = window.boxTranslations?.goToCheckout || 'Till kassan';

    btns.forEach(b => {
      b.textContent = price > 0 ? `${txt} | ${this.fmt(price)}` : txt;
      b.disabled = !ok;
    });

    const bags = this.totalQty();
    document.querySelectorAll('.js-cart-total-meals, #cartTotalMeals').forEach(el => {
      const span = el.querySelector('.js-cart-unit-text, #cartUnitText');
      if (span) span.textContent = bags > 1 ? ' produkter' : ' produkt';
      el.childNodes[0].textContent = bags + ' ';
    });
  }

  // ── Add to cart ──────────────────────────────────────────────────

  async addToCart() {
    try {
      if (!this.allFilled()) { alert('Fyll i alla boxar innan du lägger till'); return; }
      const isSub = this.selectedOption === 'subscription';
      if (isSub && this.showSubscription && !this.selectedSellingPlan) {
        alert(window.boxTranslations?.selectDeliveryFrequency || 'Välj leveransfrekvens'); return;
      }

      const sid = this.data.boxId;
      const items = [];
      // Skio reads _dynamicBoxIndex as "which copy of this box": every section of one purchase is the same box, so always 0.
      const boxIndex = 0;
      // A locked bundle tags every line (gifts included) with one instance id; the cart only removes it whole.
      const bundle = this.data.lockInCart
        ? { _bundleId: `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`, _bundle: this.data.bundleTitle || '', ...(this.data.discountCode ? { _bundleCode: this.data.discountCode } : {}) }
        : {};

      for (let i = 0; i < this.boxes.length; i++) {
        const box = this.boxes[i];
        if (!box) continue;
        const bq = this.qtyByBox[i] || {};

        Object.keys(bq).forEach(pid => {
          const qty = bq[pid];
          if (qty <= 0) return;
          const pd = this.products.find(p => p.product.id.replace('gid://shopify/Product/', '') === pid && p._boxIndex === i);
          if (!pd) return;

          const it = { id: pd.variant_id, quantity: qty, properties: { _dynamicBoxIndex: boxIndex, _dynamicBoxId: box.box_id, _dynamicBoxSectionId: sid, ...bundle } };
          if (isSub && this.showSubscription && this.selectedSellingPlan) {
            it.selling_plan = this.selectedSellingPlan.gid.replace('gid://shopify/SellingPlan/', '');
          }
          items.push(it);
        });
      }

      for (const gift of this.gifts) {
        const variantId = parseInt(gift.variantId, 10);
        if (!variantId || !Number.isFinite(variantId)) continue;
        const qty = Math.max(1, parseInt(gift.quantity, 10) || 1);
        const it = { id: variantId, quantity: qty };
        if (gift.dynamicBoxId) {
          it.properties = { _dynamicBoxIndex: boxIndex, _dynamicBoxId: gift.dynamicBoxId, _dynamicBoxSectionId: sid };
        }
        // _bundleGift lets the cart see the kit came with a bundle (locked or not).
        it.properties = { ...it.properties, ...bundle, _bundleGift: this.data.bundleTitle || '1' };
        let chosenPlan = null;
        if (gift.sellingPlanId && String(gift.sellingPlanId).trim() !== '') {
          chosenPlan = this.parseSellingPlanId(gift.sellingPlanId);
        } else if (gift.fallbackPlanGid && String(gift.fallbackPlanGid).trim() !== '') {
          chosenPlan = this.parseSellingPlanId(gift.fallbackPlanGid);
        }
        if (chosenPlan != null && Number.isFinite(chosenPlan)) {
          it.selling_plan = chosenPlan;
        }
        items.push(it);
      }

      if (!items.length) { alert('Välj minst en produkt'); return; }

      const res = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items }) });
      if (!res.ok) { const e = await res.json(); throw new Error(e.description || 'Failed to add'); }

      if (this.data.discountCode) { window.location.href = `/checkout?discount=${this.data.discountCode}`; }
      else if (window.cart?.getCart) { window.cart.getCart(); }
      else { document.querySelector('[data-cart-drawer-toggle]')?.click(); }
    } catch (e) { alert(`Error: ${e.message}`); }
  }

  // ── Info icons ───────────────────────────────────────────────────

  updateInfoIcons() {
    const mob = window.innerWidth <= 768;
    if (!mob) { document.querySelectorAll('.information-icon').forEach(i => i.classList.remove('information-icon--hidden')); return; }
    document.querySelectorAll('.product-item').forEach(item => {
      const n = item.querySelector('.product-name'), ic = item.querySelector('.information-icon');
      if (!n || !ic) return;
      ic.classList.remove('information-icon--hidden');
      const lh = parseFloat(window.getComputedStyle(n).lineHeight);
      if (!isNaN(lh) && lh > 0 ? n.getBoundingClientRect().height > lh * 1.3 : n.scrollHeight > n.clientHeight + 1) ic.classList.add('information-icon--hidden');
    });
  }

  // ── Observer ─────────────────────────────────────────────────────

  setupObserver() {
    const fb = document.querySelector('.add-to-cart-rendered'), dc = document.querySelector('.desktop-cart-container');
    if (fb && dc) new IntersectionObserver(es => es.forEach(e => fb.classList.toggle('hidden', e.isIntersecting)), { threshold: 0.1 }).observe(dc);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (!document.querySelector('.subscription-wrapper') || !window.sectionedBoxData || !window.skioDynamicBoxes || !window.bundleProducts) return;
  window.sectionedBoxWidget = new SectionedBoxWidget();
});

window.addEventListener('pageshow', e => {
  if (e.persisted || performance.getEntriesByType('navigation')[0].type === 'back_forward') window.location.href = window.location.pathname + '?scroll=top';
  if (window.location.search.includes('scroll=top')) { window.scrollTo(0, 0); window.history.replaceState({}, '', window.location.pathname); }
});
window.addEventListener('beforeunload', () => sessionStorage.clear());
